import { useEffect, useMemo, useState } from "react";
import { authRequestNbGet, refreshToken } from "../utils/helpers";
import {
  decorateTour,
  getCanonicalTourId,
  sortToursForDashboard,
} from "../utils/tourEligibility";

const mergeApplicationWithTour = (application, catalogById) => {
  const tourId = Number(application?.tourid ?? application?.tour_id ?? 0);
  const catalogTour = catalogById.get(tourId) || {};

  return {
    ...catalogTour,
    ...application,
    application_id: application?.id,
    id: tourId || catalogTour?.id || application?.id,
    tourid: tourId || catalogTour?.id || application?.tourid,
    eligibility: catalogTour?.eligibility || application?.eligibility || {},
  };
};

export default function useTourCatalog({
  endpoint = "tours",
  myToursOnly = false,
  sort = true,
} = {}) {
  const [rawTours, setRawTours] = useState([]);
  const [myTours, setMyTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const requests = [authRequestNbGet("get-my-tour")];

        if (myToursOnly) {
          requests.push(authRequestNbGet("tours"));
        } else {
          requests.push(authRequestNbGet(endpoint));
        }

        const [myResponse, tourResponse] = await Promise.all(requests);
        if (!active) return;

        if (myResponse?.token) await refreshToken(myResponse.token);
        if (tourResponse?.token) await refreshToken(tourResponse.token);

        const myRows =
          Number(myResponse?.status) === 200 && Array.isArray(myResponse?.data)
            ? myResponse.data
            : [];
        const tourRows =
          Number(tourResponse?.status) === 200 && Array.isArray(tourResponse?.data)
            ? tourResponse.data
            : [];

        setMyTours(myRows);

        if (myToursOnly) {
          const catalogById = new Map(
            tourRows
              .map((tour) => [getCanonicalTourId(tour), tour])
              .filter(([id]) => id),
          );

          setRawTours(
            myRows
              .map((application) =>
                mergeApplicationWithTour(application, catalogById),
              )
              .filter((tour) => getCanonicalTourId(tour)),
          );
        } else {
          setRawTours(tourRows);
        }
      } catch (err) {
        if (!active) return;
        setError(err?.message || "Unable to load tours.");
        setRawTours([]);
        setMyTours([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [endpoint, myToursOnly]);

  const tours = useMemo(() => {
    const decorated = rawTours.map((tour) => decorateTour(tour, myTours));
    return sort ? sortToursForDashboard(decorated) : decorated;
  }, [rawTours, myTours, sort]);

  return {
    tours,
    myTours,
    loading,
    error,
    reloadKey: `${endpoint}:${rawTours.length}:${myTours.length}`,
  };
}
